import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { 
  DollarSign, Plus, RefreshCw, Loader2, 
  Building2, Globe, AlertTriangle, CheckCircle
} from 'lucide-react';
import ExportButton from './ExportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaxManagement = ({ token }) => {
  const [configs, setConfigs] = useState([]);
  const [collected, setCollected] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAddConfig, setShowAddConfig] = useState(false);
  const [newConfig, setNewConfig] = useState({
    country: 'Canada',
    jurisdiction: '',
    tax_name: '',
    rate: ''
  });

  useEffect(() => {
    fetchConfigs();
    fetchCollected();
    fetchReport();
  }, [selectedYear]);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${API}/admin/taxes/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigs(response.data);
    } catch (error) {
      console.error('Error fetching tax configs:', error);
    }
  };

  const fetchCollected = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/taxes/collected?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollected(response.data);
    } catch (error) {
      console.error('Error fetching collected taxes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/admin/taxes/report?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching tax report:', error);
    }
  };

  const initDefaultConfigs = async () => {
    try {
      const response = await axios.post(`${API}/admin/taxes/config/init`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${response.data.inserted} configurations ajoutées`);
      fetchConfigs();
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    }
  };

  const handleAddConfig = async () => {
    try {
      await axios.post(`${API}/admin/taxes/config`, {
        ...newConfig,
        rate: parseFloat(newConfig.rate)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuration ajoutée');
      setShowAddConfig(false);
      setNewConfig({ country: 'Canada', jurisdiction: '', tax_name: '', rate: '' });
      fetchConfigs();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value);
  };

  // Group configs by country
  const configsByCountry = configs.reduce((acc, config) => {
    if (!acc[config.country]) acc[config.country] = [];
    acc[config.country].push(config);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Year Selector */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="flex items-center gap-2">
              <Label>Année fiscale:</Label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 border rounded-md"
              >
                {[2024, 2025, 2026, 2027, 2028].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <ExportButton token={token} exportType="taxes" year={selectedYear} />
              <Button variant="outline" onClick={initDefaultConfigs}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Init. configs par défaut
              </Button>
              <Button onClick={() => setShowAddConfig(true)} className="bg-brand-600">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter juridiction
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Taxes collectées</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(report.totals.collected)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Taxes remises</p>
                  <p className="text-2xl font-bold text-blue-700">{formatCurrency(report.totals.remitted)}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={`bg-gradient-to-br ${report.totals.balance_due > 0 ? 'from-red-50 to-orange-50 border-red-200' : 'from-gray-50 to-slate-50 border-gray-200'}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${report.totals.balance_due > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                    Solde à remettre
                  </p>
                  <p className={`text-2xl font-bold ${report.totals.balance_due > 0 ? 'text-red-700' : 'text-gray-700'}`}>
                    {formatCurrency(report.totals.balance_due)}
                  </p>
                </div>
                {report.totals.balance_due > 0 ? (
                  <AlertTriangle className="h-8 w-8 text-red-500" />
                ) : (
                  <CheckCircle className="h-8 w-8 text-gray-500" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Collected Taxes by Jurisdiction */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-brand-600" />
            Taxes collectées par juridiction - {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            </div>
          ) : collected && collected.by_jurisdiction.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Pays</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Juridiction</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Taxe</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Taux</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Base imposable</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Collecté</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {collected.by_jurisdiction.map((item, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">{item.country}</td>
                      <td className="py-3 px-4">{item.jurisdiction}</td>
                      <td className="py-3 px-4">{item.tax_name}</td>
                      <td className="py-3 px-4 text-right">{item.rate}%</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(item.taxable_amount)}</td>
                      <td className="py-3 px-4 text-right font-semibold text-green-600">{formatCurrency(item.tax_collected)}</td>
                      <td className="py-3 px-4 text-right">{item.transaction_count}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr className="font-bold">
                    <td colSpan={5} className="py-3 px-4">Total</td>
                    <td className="py-3 px-4 text-right text-green-600">{formatCurrency(collected.total_collected)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Aucune taxe collectée pour {selectedYear}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tax Configurations */}
      <Card>
        <CardHeader>
          <CardTitle>Configurations des taxes</CardTitle>
          <CardDescription>Taux de taxes par pays et juridiction</CardDescription>
        </CardHeader>
        <CardContent>
          {Object.keys(configsByCountry).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(configsByCountry).map(([country, countryConfigs]) => (
                <div key={country}>
                  <h4 className="font-semibold text-lg mb-2 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {country}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {countryConfigs.map((config) => (
                      <div key={config.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <span className="text-sm">{config.jurisdiction} ({config.tax_name})</span>
                        <span className="font-semibold text-brand-600">{config.rate}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Aucune configuration de taxes</p>
              <Button onClick={initDefaultConfigs} className="mt-4">
                Initialiser les configurations par défaut
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Config Dialog */}
      <AlertDialog open={showAddConfig} onOpenChange={setShowAddConfig}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ajouter une juridiction fiscale</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Pays</Label>
              <select
                value={newConfig.country}
                onChange={(e) => setNewConfig({...newConfig, country: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="Canada">Canada</option>
                <option value="USA">USA</option>
                <option value="France">France</option>
                <option value="Allemagne">Allemagne</option>
                <option value="Royaume-Uni">Royaume-Uni</option>
                <option value="Autre">Autre</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Juridiction (Province/État/Région)</Label>
              <Input
                value={newConfig.jurisdiction}
                onChange={(e) => setNewConfig({...newConfig, jurisdiction: e.target.value})}
                placeholder="Ex: Québec, California, National..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom de la taxe</Label>
                <Input
                  value={newConfig.tax_name}
                  onChange={(e) => setNewConfig({...newConfig, tax_name: e.target.value})}
                  placeholder="Ex: TVQ, GST, VAT..."
                />
              </div>
              <div className="space-y-2">
                <Label>Taux (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newConfig.rate}
                  onChange={(e) => setNewConfig({...newConfig, rate: e.target.value})}
                  placeholder="9.975"
                />
              </div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleAddConfig} className="bg-brand-600">
              Ajouter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TaxManagement;
